/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {startTransition, useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {Link as RouteLink} from 'react-router-dom'
import {
    useMultiStyleConfig,
    Box,
    Flex,
    IconButton,
    Image,
    Badge,
    useBreakpointValue,
    Drawer,
    DrawerBody,
    DrawerContent
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'

import Search from '../search'
import {AccountIcon, BasketIcon, HamburgerIcon, KsLogo, SearchIcon} from '../../components/icons'

import {HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useTagManager from '../../hooks/use-tag-manager'
import LoadingSpinner from '../loading-spinner'
import {useSiteContext} from '../../contexts/site'
import {getTotalQuantity} from '../../utils/basket-utils'
import {useUpdateShopperContext} from '../../hooks/use-update-shopper-context'
import useDeferredNavigation from '../../hooks/use-deferred-navigation'
import HeaderBorder from './partials/header-border'
import {scheduleAfterPaint, scheduleIdleAnalytics} from '../../utils/common-utils'
import {getUrl} from '../../utils/cloudinary-utils'
import {BASKET_URL} from '../../constants'

export const SearchBar = (props) => {
    const {isYR} = useSiteContext()
    const {showSearchTab, setShowSearchTab, setShowSpinner, id} = props
    const styles = useMultiStyleConfig('Header')
    const {
        app: {commerceAPI}
    } = getConfig()
    const {yrSearchBoxPlaceholder, ksSearchBoxPlaceholder} = commerceAPI.storefront

    return (
        <Box {...styles.wrapper} className="search-wrapper-box">
            <Box
                className="search-wrapper"
                {...styles.searchWrapper}
                height={showSearchTab ? 'auto' : '36px'}
            >
                <Search
                    ariaLabel={isYR ? yrSearchBoxPlaceholder : ksSearchBoxPlaceholder}
                    isMenu={props.isMenu}
                    setShowSearchTab={setShowSearchTab}
                    showSearchTab={showSearchTab}
                    id={id}
                    setShowSpinner={setShowSpinner}
                    isStickyBackSection={props.isStickyBackSection}
                    setHideCategories={props.setHideCategories}
                    hideCategories={props.hideCategories}
                    setShowCloseDrawer={props.setShowCloseDrawer}
                    showCloseDrawer={props.showCloseDrawer}
                    setShowBasket={props.setShowBasket}
                    placeholder={isYR ? yrSearchBoxPlaceholder : ksSearchBoxPlaceholder}
                    {...styles.search}
                />
            </Box>
        </Box>
    )
}

SearchBar.propTypes = {
    isMenu: PropTypes.bool,
    setShowSearchTab: PropTypes.func,
    setShowCloseDrawer: PropTypes.func,
    setHideCategories: PropTypes.func,
    hideCategories: PropTypes.bool,
    isStickyBackSection: PropTypes.bool,
    setShowBasket: PropTypes.func,
    setShowSpinner: PropTypes.func,
    showSearchTab: PropTypes.bool,
    showCloseDrawer: PropTypes.bool,
    id: PropTypes.string
}

/**
 * The header is the main source for accessing
 * navigation, search, basket, and other
 * important information and actions. It persists
 * on the top of your application and will
 * respond to changes in device size.
 *
 * To customize the styles, update the themes
 * in theme/components/project/header.js
 * @param  props
 * @param   {func} props.onMenuClick click event handler for menu button
 * @param   {func} props.onLogoClick click event handler for menu button
 * @param   {object} props.searchInputRef reference of the search input
 * @param   {func} props.onMyAccountClick click event handler for my account button
 * @param   {func} props.onMyCartClick click event handler for my cart button
 * @return  {React.ReactElement} - Header component
 */
const Header = ({children, onMenuClick = noop, onMyAccountClick = noop, onLogoClick = noop}) => {
    const intl = useIntl()
    const location = useLocation()
    const {isYR} = useSiteContext()
    const {data: basket} = useCurrentBasket()
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const styles = useMultiStyleConfig('Header')
    const tagManager = useTagManager()
    const deferredNavigate = useDeferredNavigation()
    const pdIconRef = useRef(null)

    // Handle updating the shopper context (NOTE!! Please keep it inside <GlobaleProvider> because we have to set the globale context)
    useUpdateShopperContext()

    const [showSearchTab, setShowSearchTab] = useState(false)
    const [isSearchDrawerReady, setIsSearchDrawerReady] = useState(false)
    const [showBasket, setShowBasket] = useState(true)
    const [hideCategories, setHideCategories] = useState(false)
    const [showSpinner, setShowSpinner] = useState(false)

    const {
        app: {commerceAPI}
    } = getConfig()
    const {changeUp} = commerceAPI
    const {configChangeup} = changeUp || {}
    const {purpleDot} = commerceAPI
    const {config} = purpleDot || {}

    var enablePurpleDot = false
    try {
        if (config) {
            enablePurpleDot = JSON.parse(config).enabled
        }
    } catch (e) {
        console.log(e)
    }
    var changeUpProductID = null

    try {
        const jsonConfig = JSON.parse(configChangeup)
        changeUpProductID = jsonConfig.enabled ? jsonConfig.productID : null
    } catch (e) {
        console.log(e.message)
    }

    const [totalQuantity, setTotalQuantity] = useState(0)
    const quantityTotal = basket?.productItems?.reduce((s, i) => s + i?.quantity, 0)
    const giftQtyTotal = basket?.giftCertificateItems?.length
    useEffect(() => {
        setTotalQuantity(getTotalQuantity(basket, changeUpProductID))
    }, [quantityTotal, giftQtyTotal])

    const hamburgerRef = useRef(null)
    useEffect(() => {
        if (location?.search === '?menu=open' && hamburgerRef?.current) {
            hamburgerRef.current.click()
            window.history.replaceState({}, '', location.pathname)
        }
    }, [location?.search, hamburgerRef?.current])
    useEffect(() => {
        // Note: Please remove this once we move KS to PWA
        const resetStateOnBack = (event) => {
            if (event.persisted) {
                // Page was restored from bfcache (back-forward cache)
                setShowSpinner(false)
                setTotalQuantity(getTotalQuantity(basket, changeUpProductID))
            }
        }
        window.addEventListener('pageshow', resetStateOnBack)
        return () => {
            window.removeEventListener('pageshow', resetStateOnBack)
        }
    }, [])

    useEffect(() => {
        if (!enablePurpleDot) {
            return
        }
        var CONTROL_INTERVAL_MODULE = setInterval(function () {
            /** Not a reactJS practice - We need to make this element appear if there is any PO item in the PD basket  **/
            const noOfItems = pdIconRef?.current
                ?.querySelector('purple-dot-separate-bag')
                .getAttribute('data-item-count')
            if (noOfItems && noOfItems !== '0') {
                const itemCountLabel = document.querySelector('.purpledot-banner-item-count')
                const cartPurpledotBanner = document.querySelector('.cart-purpledot-banner')
                if (itemCountLabel && cartPurpledotBanner) {
                    cartPurpledotBanner.classList.remove('hide-element')
                    itemCountLabel.textContent = `(${noOfItems})`
                    pdIconRef.current.classList.remove('hide-element')
                    clearInterval(CONTROL_INTERVAL_MODULE)
                }
            }
        }, 500) // check for every 500ms

        return () => {
            clearInterval(CONTROL_INTERVAL_MODULE)
        }
    }, [pdIconRef?.current, isYR, location?.pathname])

    let purpleDotListenersAttached = false
    useEffect(() => {
        if (purpleDotListenersAttached) return
        purpleDotListenersAttached = true

        const handleOrderCreation = (e) => {
            document.querySelector('.pd-icon').classList.add('hide-element')
            if (document.querySelector('.cart-purpledot-banner')) {
                document.querySelector('.cart-purpledot-banner').classList.add('hide-element')
            }

            tagManager.pdPurchaseEvent(e.detail)
        }

        const handleCheckoutStep = (e) => {
            tagManager.pdCheckoutStep(e.detail)
        }

        const handleRemoveFromCart = (e) => {
            if (e.detail.lineItems && e.detail.lineItems.length) {
                var items = e.detail.lineItems.length
                const itemCountLabel = document.querySelector('.purpledot-banner-item-count')
                const cartPurpledotBanner = document.querySelector('.cart-purpledot-banner')
                if (itemCountLabel && cartPurpledotBanner) {
                    cartPurpledotBanner.classList.remove('hide-element')
                    itemCountLabel.textContent = `(${items})`
                }
            } else {
                document.querySelector('.pd-icon').classList.add('hide-element')
                if (document.querySelector('.cart-purpledot-banner')) {
                    document.querySelector('.cart-purpledot-banner').classList.add('hide-element')
                }
            }
        }

        window.addEventListener('PurpleDot:PreorderCreated', handleOrderCreation)
        window.addEventListener('PurpleDot:RemoveFromCart', handleRemoveFromCart)
        window.addEventListener('PurpleDot:PreorderCheckoutStep', handleCheckoutStep)
        return () => {
            window.removeEventListener('PurpleDot:PreorderCreated', handleOrderCreation)
            window.removeEventListener('PurpleDot:RemoveFromCart', handleRemoveFromCart)
            window.removeEventListener('PurpleDot:PreorderCheckoutStep', handleCheckoutStep)
            purpleDotListenersAttached = false
        }
    }, [])

    useEffect(() => {
        if (showSearchTab) {
            document.querySelector('html').style.overflow = 'hidden'
        } else {
            document.querySelector('html').style.overflow = ''
        }
    }, [showSearchTab])

    useEffect(() => {
        let isCancelled = false
        let deferredReadyId

        if (!showSearchTab) {
            setIsSearchDrawerReady(false)
            return undefined
        }

        deferredReadyId = scheduleAfterPaint(() => {
            if (!isCancelled) {
                startTransition(() => {
                    setIsSearchDrawerReady(true)
                })
            }
        })

        return () => {
            isCancelled = true

            if (typeof deferredReadyId !== 'number' || typeof window === 'undefined') {
                return
            }

            if (typeof window.cancelAnimationFrame === 'function') {
                window.cancelAnimationFrame(deferredReadyId)
            } else {
                window.clearTimeout(deferredReadyId)
            }
        }
    }, [showSearchTab])

    const initialFocusRef = useRef(null)
    const [actionSpinner, showActionSpinner] = useState(false)

    return (
        <Box
            {...styles.container}
            className="header-wrapper-index"
            backgroundColor={{base: isYR ? 'black.300' : 'white', lg: 'white'}}
            borderTop={{base: isYR ? '0.5px solid' : '0px', lg: 0}}
            borderBottom={{lg: '0.5px solid'}}
            borderColor="gray.400 !important"
            position="relative"
        >
            {actionSpinner && <LoadingSpinner position="fixed" top="auto" />}
            <Box {...styles.content} alignContent="center" className="header-flex-box">
                <Flex
                    {...styles.iconsWrapper}
                    marginBottom={{base: '3px !important', lg: 'unset'}}
                    className="header-flex"
                >
                    <IconButton
                        aria-label={intl.formatMessage({
                            id: 'header.button.assistive_msg.logo',
                            defaultMessage: 'Logo'
                        })}
                        icon={
                            isYR ? (
                                <Image
                                    {...styles.logo}
                                    alt="Yellow Rose Logo"
                                    src={getUrl('UIImages/images/small-yr-logo.svg')}
                                />
                            ) : (
                                <KsLogo {...styles.logo} alt="Kendra Scott Logo" />
                            )
                        }
                        {...styles.icons.main}
                        {...styles.icons.desktopBrandLogo}
                        className="header-icon-button header-brand-logo desktop-logo"
                        onClick={onLogoClick}
                    />
                    <IconButton
                        aria-label={intl.formatMessage({
                            id: 'header.button.assistive_msg.logo',
                            defaultMessage: 'Logo'
                        })}
                        icon={
                            isYR ? (
                                <>
                                    <Image
                                        {...styles.logo}
                                        src={getUrl('UIImages/images/full-yr-logo.svg')}
                                        width="171px"
                                        height="34px"
                                        alt="Yellow Rose Logo"
                                        display={{xxs: 'block', base: 'none'}}
                                        verticalAlign="middle"
                                    />

                                    <Image
                                        {...styles.logo}
                                        alt="Yellow Rose Logo"
                                        width="31px"
                                        height="36px"
                                        display={{xxs: 'none', base: 'block'}}
                                        src={getUrl('UIImages/images/small-yr-logo-white.svg')}
                                    />
                                </>
                            ) : (
                                <>
                                    <Image
                                        {...styles.logo}
                                        src={getUrl('UIImages/images/full-ks-logo.svg')}
                                        width="193px"
                                        height="60px"
                                        alt="Kendra Scott Logo"
                                        display={{xxs: 'block', base: 'none'}}
                                        verticalAlign="middle"
                                    />
                                    <KsLogo
                                        {...styles.logo}
                                        alt="Kendra Scott Logo"
                                        width="33px"
                                        height="33px"
                                        display={{xxs: 'none', base: 'block'}}
                                    />
                                </>
                            )
                        }
                        {...styles.icons.mobileBrandLogo}
                        onClick={onLogoClick}
                        className="header-icon-button header-brand-logo mobile-logo"
                    />
                    <Box
                        display={{lg: hideCategories ? 'none' : 'block'}}
                        {...styles.bodyContainer}
                        className="body-container-box"
                    >
                        {children}
                    </Box>
                    {/* This Spinner will be shown for the Phase 1 only if user is going to SFRA URLs via search. */}
                    {showSpinner && <LoadingSpinner />}
                    <Drawer
                        isOpen={showSearchTab}
                        onClose={() => {
                            startTransition(() => {
                                setIsSearchDrawerReady(false)
                                setShowSearchTab(false)
                            })
                        }}
                        placement="left"
                        size="full"
                        motionPreset={{base: 'slideInBottom', md: 'slideInRight'}}
                        scrollBehavior="inside"
                        blockScrollOnMount={true}
                        initialFocusRef={initialFocusRef}
                        // Add the following 1 line to fix the Safari behavior of Drawer Close
                        returnFocusOnClose={false}
                        className="header-search-drawer"
                    >
                        <DrawerContent maxW="100vw" className="header-search-drawer-content">
                            <DrawerBody padding="1rem 0px" className="header-search-drawer-body">
                                {isSearchDrawerReady ? (
                                    <SearchBar
                                        setShowSearchTab={(bool) => {
                                            setShowSearchTab(bool)
                                        }}
                                        id="header-mobile"
                                        setShowSpinner={(bool) => {
                                            setShowSpinner(bool)
                                        }}
                                        setShowBasket={(bool) => {
                                            setShowBasket(bool)
                                        }}
                                        setHideCategories={(bool) => {
                                            setHideCategories(bool)
                                        }}
                                        showSearchTab={showSearchTab}
                                        hideCategories={hideCategories}
                                    />
                                ) : (
                                    <Box minH="36px" />
                                )}
                            </DrawerBody>
                        </DrawerContent>
                    </Drawer>
                    <IconButton
                        aria-label={intl.formatMessage({
                            id: 'header.button.assistive_msg.search',
                            defaultMessage: 'Search'
                        })}
                        className="open-search-mobile"
                        icon={
                            <SearchIcon
                                alt="Search Logo"
                                width="15px"
                                height="15px"
                                verticalAlign="middle"
                                color={isYR ? 'white' : 'black'}
                            />
                        }
                        {...styles.icons.main}
                        {...styles.icons.mobileSearchLogo}
                        minWidth="33px"
                        margin="0"
                        onClick={() => {
                            startTransition(() => setShowSearchTab(true))
                            scheduleIdleAnalytics(() => {
                                tagManager.formFieldChange('simpleSearch', 'Form Submit Intent')
                            })
                        }}
                    />
                    <HideOnMobile
                        marginLeft="auto"
                        width={{lg: hideCategories ? '90%' : 'auto', xl: 'auto'}}
                    >
                        <Box className="search-bar-box">
                            <SearchBar
                                setShowSearchTab={(bool) => {
                                    setShowSearchTab(bool)
                                }}
                                id="header-desktop"
                                setShowSpinner={(bool) => {
                                    setShowSpinner(bool)
                                }}
                                setShowBasket={(bool) => {
                                    setShowBasket(bool)
                                }}
                                setHideCategories={(bool) => {
                                    setHideCategories(bool)
                                }}
                                hideCategories={hideCategories}
                                showSearchTab={showSearchTab}
                            />
                        </Box>
                    </HideOnMobile>
                    <IconButton
                        icon={
                            <AccountIcon
                                alt="User Icon"
                                width="16px"
                                height="16px"
                                verticalAlign="middle"
                                color={isYR ? 'white' : 'black'}
                            />
                        }
                        aria-label={intl.formatMessage({
                            id: 'header.button.assistive_msg.my_account',
                            defaultMessage: 'My Account'
                        })}
                        variant="unstyled"
                        display={{lg: 'none'}}
                        {...styles.icons.main}
                        minWidth="33px"
                        margin="0"
                        className="header-icon-button my-account-logo"
                        onClick={onMyAccountClick}
                    />
                    <IconButton
                        as={RouteLink}
                        to={BASKET_URL}
                        onClick={(event) => deferredNavigate(event, BASKET_URL)}
                        sx={{
                            'z-index': showBasket ? 'auto' : '-1'
                        }}
                        _hover={{
                            lg: {
                                background: 'grayAlpha5',
                                borderRadius: '5px'
                            }
                        }}
                        opacity={{
                            lg: showBasket && !hideCategories ? 1 : 0,
                            xl: showBasket && !hideCategories ? 1 : 0
                        }}
                        pointerEvents={{lg: showBasket && !hideCategories ? 'auto' : 'none'}}
                        className="basket-icon"
                        textAlign="center"
                        data-cart-value={basket?.productTotal || 0}
                        aria-label={intl.formatMessage(
                            {
                                id: 'header.button.assistive_msg.my_cart_with_num_items',
                                defaultMessage: 'My cart, number of items: {numItems}'
                            },
                            {numItems: totalQuantity}
                        )}
                        icon={
                            <>
                                <BasketIcon
                                    alt="Bag icon"
                                    verticalAlign="middle"
                                    color={{base: isYR ? 'white' : 'black', lg: 'black'}}
                                    width={{base: '14px', lg: '18px'}}
                                    height={{base: '16px', lg: '20px'}}
                                />
                                {basket && totalQuantity > 0 && (
                                    <Badge
                                        variant="notification"
                                        className="total-items"
                                        color={isYR && !isDesktop ? 'black' : 'white'}
                                        backgroundColor={isYR && !isDesktop ? 'white' : 'black'}
                                        fontSize="9px"
                                        top="0.18rem"
                                        right="0.2rem"
                                    >
                                        {totalQuantity}
                                    </Badge>
                                )}
                            </>
                        }
                        variant="unstyled"
                        {...styles.icons.main}
                        margin="0"
                        padding="0.7rem"
                        minWidth="33px"
                    />
                    {enablePurpleDot ? (
                        <IconButton
                            sx={{
                                'z-index': showBasket ? 'auto' : '-1',
                                'purple-dot-separate-bag svg': {
                                    width: {base: '16px', lg: '18px'},
                                    height: {base: '20px', lg: '20px'},
                                    stroke: {base: isYR ? 'white' : 'black', lg: 'black'}
                                },
                                '.purple-dot-separate-bag__indicator': {
                                    width: '5px',
                                    height: '5px',
                                    top: '6px',
                                    backgroundColor: {
                                        base: isYR ? 'white' : 'purple.90',
                                        lg: 'purple.90'
                                    },
                                    right: '-1px'
                                }
                            }}
                            _hover={{
                                lg: {
                                    background: 'grayAlpha5',
                                    borderRadius: '5px'
                                }
                            }}
                            opacity={{
                                lg: showBasket && !hideCategories ? 1 : 0,
                                xl: showBasket && !hideCategories ? 1 : 0
                            }}
                            pointerEvents={{lg: showBasket && !hideCategories ? 'auto' : 'none'}}
                            className="pd-icon hide-element"
                            ref={pdIconRef}
                            onClick={() => {
                                tagManager.openPdBasket('header')
                            }}
                            aria-label="Purple Dot Header Icon"
                            icon={<purple-dot-separate-bag />}
                            variant="unstyled"
                            {...styles.icons.main}
                            margin="0px"
                            alignContent="space-around"
                            textAlign="center"
                            minWidth="33px"
                        />
                    ) : (
                        <></>
                    )}
                    <IconButton
                        aria-label={intl.formatMessage({
                            id: 'header.button.assistive_msg.menu',
                            defaultMessage: 'Menu'
                        })}
                        ref={hamburgerRef}
                        title={intl.formatMessage({
                            id: 'header.button.assistive_msg.menu.open_dialog',
                            defaultMessage: 'Opens a dialog'
                        })}
                        icon={
                            <HamburgerIcon
                                alt="hamburger"
                                width="18px"
                                height="16px"
                                verticalAlign="middle"
                                color={isYR ? 'white' : 'black'}
                            />
                        }
                        {...styles.menu}
                        {...styles.icons.main}
                        className="header-icon-button hamburger-icon"
                        onClickCapture={() => {
                            showActionSpinner(true)
                            scheduleAfterPaint(() => {
                                startTransition(() => onMenuClick())
                                setTimeout(() => {
                                    showActionSpinner(false)
                                }, 100)
                            })
                        }}
                    />
                </Flex>
            </Box>
            <HeaderBorder />
        </Box>
    )
}

Header.propTypes = {
    children: PropTypes.node,
    onMenuClick: PropTypes.func,
    onLogoClick: PropTypes.func,
    onMyAccountClick: PropTypes.func,
    isStickyBackSection: PropTypes.bool,
    onMyCartClick: PropTypes.func,
    searchInputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.elementType})
    ])
}

export default Header
